<template>
  <div class="flex md:flex-row gap-y-1" :class="[fullWidth && '!w-full']" v-show="isShow">
    <div :class="direction" class="w-full flex gap-4 gap-y-1 text-brand-black-secondary">
      <label
        v-if="label"
        :for="fieldName"
        :class="labelClass"
        class="w-full md:w-1/3 flex items-center gap-x-2"
      >
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full relative flex flex-wrap gap-3">
        <div
          :key="key"
          v-for="(optionItem, key) in optionsList"
          class="cursor-pointer flex items-center gap-2 accent-primary"
        >
          <input
            type="radio"
            :disabled="isDisabled"
            :key="optionItem.value"
            :value="optionItem.value"
            :checked="inputValue === optionItem.value"
            @change="handleChange(optionItem.value)"
            @blur="handleFieldBlur"
          />
          <div @click.stop="handleChange(optionItem.value)" class="text-wrap">
            {{ i18n.t(optionItem.label) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import HintTippy from '@/components/Form/HintTyppy.vue'
import useRadioGroupActions from '@/composables/actions/useRadioGroupActions.js'
import useRadioGroupOptions from '@/composables/useRadioGroupOptions.js'
import useRuleHandler from '@/composables/useRuleHandler'
import useVisible from '@/composables/useVisible'
import useWatchAction from '@/composables/watchActions/index'
import axiosInstance from '@/services'
import { useFormStore } from '@/stores/form'
import { useField } from 'vee-validate'
import { computed, onMounted, ref, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const props = defineProps({
  fullWidth: {
    type: Boolean,
    default: false
  },
  hint: String,
  labelValue: String,
  radioOptions: {
    type: Array,
    required: true
  },
  options: Object,
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  modelValue: {
    type: [String, Number]
  },
  trigger: String,
  on_change: Object,
  rules: Array,
  showRules: Object,
  defaultValue: String,
  direction: {
    type: String,
    default: 'flex-col md:flex-row md:items-start'
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  dependentOn: String,
  isDisabled: Boolean,
  debounceTime: {
    type: Number,
    default: () => 0
  },
  onWatch: Object
})

const formStore = useFormStore()
const fieldName = toRef(props, 'fieldName')
const i18n = useI18n()
const { findShowRule, getBindObj } = useVisible()
const route = useRoute()
const optionsList = ref([])

const {
  value: inputValue,
  errorMessage,
  handleBlur: handleFieldBlur,
  handleChange: handleRadioChange,
  meta
} = useField(fieldName, (value) => useRuleHandler(value, props.rules), {
  initialValue: props.defaultValue
})

// Process value from dynamic fields, then replace the patch with these.
const processPathQuery = (queryPath) => {
  if (!queryPath) return
  let newQueryPath = ''
  if (Object.keys(queryPath).length > 0) {
    Object.keys(queryPath).map((key) => {
      if (queryPath[key].ref) {
        newQueryPath += `/${formStore.extraMap[queryPath[key].ref]}`
      } else {
        newQueryPath += queryPath[key]
      }
    })
  }
  return newQueryPath
}

// Process value from dynamic fields, then pass to query of the api.
const processQuery = (query) => {
  if (!query) return
  const newQuery = {}
  if (Object.keys(query).length > 0) {
    Object.keys(query).map((key) => {
      if (query[key] == '__serviceTransactionId__') {
        newQuery[key] = route.params.serviceId
        return
      }

      if (query[key].ref) {
        newQuery[key] = formStore.extraMap[query[key].ref] || formStore.form.values[query[key].ref]
      } else {
        newQuery[key] = query[key]
      }
    })
  }
  return newQuery
}

onMounted(async () => {
  if ((props.radioOptions || []).length > 0) {
    optionsList.value = props.radioOptions
    return
  }

  if (Object.keys(props.options).length > 0) {
    const { path, query, body, method } = props.options.api
    let newPath = path
    if (formStore.form.values[props.dependentOn] && newPath.includes('__reactive__')) {
      newPath = newPath.replaceAll('__reactive__', formStore.form.values[props.dependentOn])
    }

    if (props.options.api.path_query) {
      newPath += processPathQuery(props.options.api.path_query)
    }
    const newQuery = processQuery(query)
    const newData = processQuery(body)

    const requestConfig = {
      method,
      url: newPath,
      data: newData,
      params: newQuery
    }

    const response = await axiosInstance.request(requestConfig)

    if (props.options.api.on_success?.action) {
      optionsList.value =
        await useRadioGroupOptions()[props.options.api.on_success?.action](response)
    } else {
      optionsList.value = response
    }
  }
})

const handleChange = async (currentValue) => {
  if (props.isDisabled) return
  handleRadioChange(currentValue)
  if (props.trigger == 'change') {
    if (props.on_change.action) {
      await useRadioGroupActions()[props.on_change.action]()
    }
  }
}

const isShow = computed(() => {
  if (props.showRules) {
    return findShowRule(
      props.showRules.any || props.showRules.all,
      props.showRules.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )
  } else {
    return true
  }
})
const t9nLabel = computed(() => i18n.t(props.label))

if (props.trigger == 'watch') {
  useWatchAction()[props.onWatch.action]()
}

const isRequired = computed(() => {
  const requiredReactive = props.rules?.find((r) => r.name == 'required_reactive')
  if (requiredReactive) {
    return isRequiredByReactive(requiredReactive)
  }
  return !!props.rules?.find((r) => ['required', 'required_ui'].includes(r.name))
})

/**
 * provides support for the required rule based on certain values
 * reason:
 * due to sharing forms, it's inevitable to have clashing logic on
 * some of the schema. this is a way to support both while still
 * enabling sharing between services.
 * {
 *     "name": "required_reactive",
 *     "value": {
 *         "operator": "in",
 *         "value": [
 *             33
 *         ],
 *         "ref": "serviceTransactionId"
 *     },
 *     "message": "INVALID_REQUIRED"
 * }
 */
const isRequiredByReactive = (rule) => {
  const { operator, value, ref } = rule.value

  let compareAgainst
  if (ref == 'serviceTransactionId') {
    compareAgainst = route.params.serviceId
  } else {
    compareAgainst = formStore.form.values[ref]
  }

  switch (operator) {
    case 'in':
      return value.includes(compareAgainst)
    case 'equal':
      return compareAgainst == value
    case 'not_in':
      return [undefined, ...value].includes(compareAgainst) === false
    default:
      return false
  }
}
</script>

<style lang="scss" scoped></style>
